/**
 * DATEV-EXTF "Debitoren/Kreditoren" — gemeinsames Formatwissen von Import und
 * Export.
 *
 * Quelle ist ein echter Kanzlei-Export (EXTF_K_..._Ehlers.csv): Zeile 1 traegt
 * die EXTF-Metadaten, Zeile 2 den Spaltenkopf mit ueber 200 benannten Spalten,
 * ab Zeile 3 die Daten. Semikolon-getrennt, quotiert wo noetig.
 *
 * HARTE REGEL: Spaltenpositionen werden IMMER ueber den Spaltennamen aus dem
 * Kopf ermittelt, nie als Index konstant gehalten — DATEV verschiebt Spalten
 * zwischen Formatversionen, und die Datei bringt ihren Kopf selbst mit.
 */

import type { InvoiceDatevConfig } from '../types';
// Beide Helfer kommen bewusst aus dem Buchungsstapel-Exporter und werden hier
// NICHT nachgebaut: Debitorenstamm und Buchungsstapel muessen dieselbe
// Beraternummer, dieselbe Mandantennummer und denselben Wirtschaftsjahresbeginn
// tragen, sonst ordnet DATEV die beiden Dateien nicht demselben Mandanten zu.
// Zwei Formatierungen waeren zwei Fehlerquellen.
import { formatYearMonthDay, onlyDigits } from './datev/datevExport';

/**
 * Spaltenkopf (Zeile 2) verbatim aus der Beispieldatei. Beim Export schreiben
 * wir genau diesen Kopf, beim Import wird der Kopf der FREMDEN Datei benutzt.
 */
export const EXTF_HEADER_LINE =
    'Konto;Name (Adressattyp Unternehmen);Unternehmensgegenstand;Name (Adressattyp natürl. Person);Vorname (Adressattyp natürl. Person);Name (Adressattyp keine Angabe);Adressattyp;Kurzbezeichnung;EU-Land;EU-UStID;Anrede;Titel/Akad. Grad;Adelstitel;Namensvorsatz;Adressart;Straße;Postfach;Postleitzahl;Ort;Land;Versandzusatz;Adresszusatz;Abweichende Anrede;Abw. Zustellbezeichnung 1;Abw. Zustellbezeichnung 2;Kennz. Korrespondenzadresse;Adresse Gültig von;Adresse Gültig bis;Telefon;Bemerkung (Telefon);Telefon GL;Bemerkung (Telefon GL);E-Mail;Bemerkung (E-Mail);Internet;Bemerkung (Internet);Fax;Bemerkung (Fax);Sonstige;Bemerkung (Sonstige);Bankleitzahl 1;Bankbezeichnung 1;Bank-Kontonummer 1;Länderkennzeichen 1;IBAN-Nr. 1;Leerfeld;SWIFT-Code 1;Abw. Kontoinhaber 1;Kennz. Hauptbankverb. 1;Bankverb 1 Gültig von;Bankverb 1 Gültig bis;Bankleitzahl 2;Bankbezeichnung 2;Bank-Kontonummer 2;Länderkennzeichen 2;IBAN-Nr. 2;Leerfeld;SWIFT-Code 2;Abw. Kontoinhaber 2;Kennz. Hauptbankverb. 2;Bankverb 2 Gültig von;Bankverb 2 Gültig bis;Bankleitzahl 3;Bankbezeichnung 3;Bank-Kontonummer 3;Länderkennzeichen 3;IBAN-Nr. 3;Leerfeld;SWIFT-Code 3;Abw. Kontoinhaber 3;Kennz. Hauptbankverb. 3;Bankverb 3 Gültig von;Bankverb 3 Gültig bis;Bankleitzahl 4;Bankbezeichnung 4;Bank-Kontonummer 4;Länderkennzeichen 4;IBAN-Nr. 4;Leerfeld;SWIFT-Code 4;Abw. Kontoinhaber 4;Kennz. Hauptbankverb. 4;Bankverb 4 Gültig von;Bankverb 4 Gültig bis;Bankleitzahl 5;Bankbezeichnung 5;Bank-Kontonummer 5;Länderkennzeichen 5;IBAN-Nr. 5;Leerfeld;SWIFT-Code 5;Abw. Kontoinhaber 5;Kennz. Hauptbankverb. 5;Bankverb 5 Gültig von;Bankverb 5 Gültig bis;Leerfeld;Briefanrede;Grußformel;Kunden-/Lief.-Nr.;Steuernummer;Sprache;Ansprechpartner;Vertreter;Sachbearbeiter;Diverse-Konto;Ausgabeziel;Währungssteuerung;Kreditlimit (Debitor);Zahlungsbedingung;Fälligkeit in Tagen (Debitor);Skonto in Prozent (Debitor);Kreditoren-Ziel 1 Tg.;Kreditoren-Skonto 1 %;Kreditoren-Ziel 2 Tg.;Kreditoren-Skonto 2 %;Kreditoren-Ziel 3 Brutto Tg.;Kreditoren-Ziel 4 Tg.;Kreditoren-Skonto 4 %;Kreditoren-Ziel 5 Tg.;Kreditoren-Skonto 5 %;Mahnung;Kontoauszug;Mahntext 1;Mahntext 2;Mahntext 3;Kontoauszugstext;Mahnlimit Betrag;Mahnlimit %;Zinsberechnung;Mahnzinssatz 1;Mahnzinssatz 2;Mahnzinssatz 3;Lastschrift;Leerfeld;Mandantenbank;Zahlungsträger;Indiv. Feld 1;Indiv. Feld 2;Indiv. Feld 3;Indiv. Feld 4;Indiv. Feld 5;Indiv. Feld 6;Indiv. Feld 7;Indiv. Feld 8;Indiv. Feld 9;Indiv. Feld 10;Indiv. Feld 11;Indiv. Feld 12;Indiv. Feld 13;Indiv. Feld 14;Indiv. Feld 15;Abweichende Anrede (Rechnungsadresse);Adressart (Rechnungsadresse);Straße (Rechnungsadresse);Postfach (Rechnungsadresse);Postleitzahl (Rechnungsadresse);Ort (Rechnungsadresse);Land (Rechnungsadresse);Versandzusatz (Rechnungsadresse);Adresszusatz (Rechnungsadresse);Abw. Zustellbezeichnung 1 (Rechnungsadresse);Abw. Zustellbezeichnung 2 (Rechnungsadresse);Adresse Gültig von (Rechnungsadresse);Adresse Gültig bis (Rechnungsadresse);Bankleitzahl 6;Bankbezeichnung 6;Bank-Kontonummer 6;Länderkennzeichen 6;IBAN-Nr. 6;Leerfeld;SWIFT-Code 6;Abw. Kontoinhaber 6;Kennz. Hauptbankverb. 6;Bankverb 6 Gültig von;Bankverb 6 Gültig bis;Bankleitzahl 7;Bankbezeichnung 7;Bank-Kontonummer 7;Länderkennzeichen 7;IBAN-Nr. 7;Leerfeld;SWIFT-Code 7;Abw. Kontoinhaber 7;Kennz. Hauptbankverb. 7;Bankverb 7 Gültig von;Bankverb 7 Gültig bis;Bankleitzahl 8;Bankbezeichnung 8;Bank-Kontonummer 8;Länderkennzeichen 8;IBAN-Nr. 8;Leerfeld;SWIFT-Code 8;Abw. Kontoinhaber 8;Kennz. Hauptbankverb. 8;Bankverb 8 Gültig von;Bankverb 8 Gültig bis;Bankleitzahl 9;Bankbezeichnung 9;Bank-Kontonummer 9;Länderkennzeichen 9;IBAN-Nr. 9;Leerfeld;SWIFT-Code 9;Abw. Kontoinhaber 9;Kennz. Hauptbankverb. 9;Bankverb 9 Gültig von;Bankverb 9 Gültig bis;Bankleitzahl 10;Bankbezeichnung 10;Bank-Kontonummer 10;Länderkennzeichen 10;IBAN-Nr. 10;Leerfeld;SWIFT-Code 10;Abw. Kontoinhaber 10;Kennz. Hauptbankverb. 10;Bankverb 10 Gültig von;Bankverb 10 Gültig bis;Nummer Fremdsystem;Insolvent;SEPA-Mandatsreferenz 1;SEPA-Mandatsreferenz 2;SEPA-Mandatsreferenz 3;SEPA-Mandatsreferenz 4;SEPA-Mandatsreferenz 5;SEPA-Mandatsreferenz 6;SEPA-Mandatsreferenz 7;SEPA-Mandatsreferenz 8;SEPA-Mandatsreferenz 9;SEPA-Mandatsreferenz 10;Verknüpftes OPOS-Konto;Mahnsperre bis;Lastschriftsperre bis;Zahlungssperre bis;Gebührenberechnung;Mahngebühr 1;Mahngebühr 2;Mahngebühr 3;Pauschalenberechnung;Verzugspauschale 1;Verzugspauschale 2;Verzugspauschale 3;Alternativer Suchname;Status;Anschrift manuell geändert (Korrespondenzadresse);Anschrift individuell (Korrespondenzadresse);Anschrift manuell geändert (Rechnungsadresse);Anschrift individuell (Rechnungsadresse);Fristberechnung bei Debitor;Mahnfrist 1;Mahnfrist 2;Mahnfrist 3;Letzte Frist';

/** Spaltennamen unseres Exports in Reihenfolge. */
export const EXTF_COLUMNS: string[] = EXTF_HEADER_LINE.split(';');

/**
 * Zeile 1 unseres Exports. Struktur aus der Beispieldatei uebernommen, nur der
 * Zeitstempel und die Mandantenangaben sind dynamisch.
 *
 * Feldanzahl 31 nach DATEV-Formatkopf; die Beispieldatei fuellt danach nur
 * noch Leerfelder.
 */
const EXTF_META_FIELDS = 31;

/** Zeitstempel im DATEV-Format JJJJMMTThhmmssfff (lokale Zeit). */
const extfTimestamp = (now: Date): string => {
    const p = (n: number, w = 2) => String(n).padStart(w, '0');
    return (
        `${now.getFullYear()}${p(now.getMonth() + 1)}${p(now.getDate())}` +
        `${p(now.getHours())}${p(now.getMinutes())}${p(now.getSeconds())}${p(now.getMilliseconds(), 3)}`
    );
};

/**
 * Berater- und Mandantennummer, Wirtschaftsjahresbeginn und Sachkontenlaenge
 * kommen aus derselben DATEV-Konfiguration wie der Buchungsstapel. Sie muessen
 * uebereinstimmen, sonst passen die beiden Dateien beim Import nicht zusammen.
 *
 * OHNE Konfiguration wird bewusst abgebrochen und NICHT auf die frueheren
 * Platzhalter (9999999/99999) zurueckgefallen: eine Datei, die beim
 * Steuerberater auf einer Phantom-Beraternummer landet, laesst sich nicht
 * importieren und niemand merkt es hier. Ein Abbruch mit Ansage ist der
 * deutlich guenstigere Fehler.
 */
export const buildExtfMetaLine = (
    config: InvoiceDatevConfig | undefined,
    now: Date = new Date(),
): string => {
    if (!config) {
        throw new Error(
            'Keine DATEV-Konfiguration hinterlegt. Beraternummer, Mandantennummer und ' +
                'Wirtschaftsjahresbeginn sind für den Debitorenstamm zwingend.',
        );
    }
    const fields = new Array<string>(EXTF_META_FIELDS).fill('');
    fields[0] = 'EXTF';
    fields[1] = '700';                    // Versionsnummer
    fields[2] = '16';                     // Formatkategorie Debitoren/Kreditoren
    fields[3] = 'Debitoren/Kreditoren';
    fields[4] = '5';                      // Formatversion
    fields[5] = extfTimestamp(now);       // erzeugt am
    fields[7] = 'SE';                     // Herkunft
    fields[8] = 'AA';                     // exportiert von
    // Ungequotet in der Zeile: ein Semikolon aus den Einstellungen wuerde die
    // Feldstruktur der Kopfzeile zerlegen, und die Feldzaehlung sieht das nicht
    // (sie zaehlt vor dem join). Darum derselbe Waechter wie im Buchungsstapel.
    fields[10] = onlyDigits(config.consultantNumber, 'Beraternummer');
    fields[11] = onlyDigits(config.clientNumber, 'Mandantennummer');
    fields[12] = formatYearMonthDay(config.fiscalYearStart, 'Wirtschaftsjahresbeginn');
    fields[13] = String(config.accountLength); // Sachkontenlaenge
    // Diese drei stammen aus einem echten Kanzlei-Export und bleiben stehen.
    // Die Referenzdatei laesst sie leer — das ist KEIN Grund, sie hier zu
    // loeschen; bitte nicht "aufraeumen".
    fields[19] = '0';
    fields[25] = '521582';
    fields[26] = '40';
    return fields.join(';');
};

/** Excel-Formelfehler in der Quelldatei — zaehlen als leeres Feld. */
const ERROR_VALUES = new Set(['#NAME?', '#REF!', '#WERT!', '#VALUE!', '#NV', '#N/A', '#DIV/0!']);

/** Zellwert bereinigt: getrimmt, Formelfehler werden zu ''. */
export const cleanExtfValue = (v: string | undefined): string => {
    const t = (v ?? '').trim();
    return ERROR_VALUES.has(t.toUpperCase()) ? '' : t;
};

/**
 * Spaltenindex ueber den Namen. Vergleich getrimmt und case-insensitiv; der
 * ERSTE Treffer gewinnt (Namen wie "Leerfeld" kommen mehrfach vor, die
 * fachlichen Spalten sind eindeutig).
 */
export const extfColumnIndex = (columns: string[], name: string): number =>
    columns.findIndex((c) => c.trim().toLowerCase() === name.trim().toLowerCase());

/** Spaltennamen, die Import und Export beide brauchen. */
export const EXTF_COL = {
    account: 'Konto',
    companyName: 'Name (Adressattyp Unternehmen)',
    personLastName: 'Name (Adressattyp natürl. Person)',
    personFirstName: 'Vorname (Adressattyp natürl. Person)',
    unknownName: 'Name (Adressattyp keine Angabe)',
    addressType: 'Adressattyp',
    shortName: 'Kurzbezeichnung',
    salutation: 'Anrede',
    title: 'Titel/Akad. Grad',
    addressKind: 'Adressart',
    street: 'Straße',
    zip: 'Postleitzahl',
    city: 'Ort',
    country: 'Land',
    phone: 'Telefon',
    email: 'E-Mail',
    language: 'Sprache',
} as const;

/** Kurzbezeichnung ist in DATEV 15 Zeichen lang. */
export const EXTF_SHORT_NAME_MAX = 15;

/**
 * Firmen-Heuristik fuer Rohnamen ohne verlaessliches Typ-Signal.
 *
 * Noetig, weil die Kanzlei-Datei den Adressattyp NICHT pflegt: dort steht bei
 * jeder Zeile 1 (natuerliche Person), und der volle Firmenname steht doppelt in
 * Name UND Vorname. Ohne diese Heuristik wuerde "A.H.R. 5 GmbH & Co. KG" als
 * Privatperson mit Vorname "A.H.R. 5 GmbH &" importiert.
 *
 * Zusaetzliche Regel: bleiben nach Anrede/Titel mehr als drei Namensteile
 * uebrig, ist es keine Privatperson mehr ("Arbeitsgemeinschaft Neubau Leitz").
 */
const LEGAL_FORMS =
    /(^|[\s.,])(gmbh|mbh|ag|kg|kgaa|ohg|gbr|ug|se|eg|e\.?\s?k\.?|e\.?\s?v\.?|ltd|inc|llc|plc|co)([\s.,]|$)/i;

const COMPANY_WORDS =
    /(stiftung|verein|verband|genossenschaft|gesellschaft|arbeitsgemeinschaft|arge|amt|stadt|gemeinde|landkreis|kreisverwaltung|behörde|behoerde|institut|klinik|krankenhaus|praxis|apotheke|hotel|immobilien|hausverwaltung|verwaltung|service|systeme|holding|partner|bau|werk|technik|handel|handwerk|betrieb|ingenieur|architekt|kanzlei|steuerberat|logistik|transport|energie|elektro|sanitär|sanitaer|garten|reinigung|consult|projekt|wohnung)/i;

export const looksLikeCompanyName = (raw: string, remainingTokens?: number): boolean => {
    const name = (raw ?? '').trim();
    if (!name) return false;
    if (name.includes('&')) return true;
    if (LEGAL_FORMS.test(name)) return true;
    if (COMPANY_WORDS.test(name)) return true;
    if ((remainingTokens ?? 0) > 3) return true;
    return false;
};

/** Wert fuer eine EXTF-Zelle escapen (Semikolon/Quote/Umbruch). */
export const escapeExtfValue = (v: string): string =>
    /[;"\r\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v;
